import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Map, Overlay } from 'pigeon-maps';
import * as d3 from 'd3';
import { SelectCurrentCoordinates } from '_redux/slices/timeline/timeline-live-location-tab';
import { fetchDeviceEvents } from '_services/coverage-service';
import { resolveWifiEventsToCoords, resolveGatewayName, bleToCoords, matchBle } from './asset-tracking-utils';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';

// RSSI: -120 dBm (weak/red) → -50 dBm (strong/green)
const rssiScale = d3.scaleSequential().domain([-120, -50]).interpolator(d3.interpolateRdYlGn).clamp(true);
// SNR: -20 dB (weak/red) → +15 dB (strong/green)
const snrScale = d3.scaleSequential().domain([-20, 15]).interpolator(d3.interpolateRdYlGn).clamp(true);

const OSM_PROVIDER = (x, y, z) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

function tsToDate(at) {
    return at?.toDate ? at.toDate() : new Date((at?.seconds ?? 0) * 1000);
}
const SATELLITE_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

function processEvents(events, locations, wifiCoords) {
    return events.flatMap((evt, i) => {

        const gws = evt.foundGateways ?? [];
        const gw = [...gws].sort((a, b) => (b.rssi ?? -999) - (a.rssi ?? -999))[0];

        if (!gw) return [];
        const rssi = gw.rssi ?? null;
        const snr  = gw.snr  ?? null;
        const gatewayId = gw.gatewayId ?? null;
        const allGateways = gws.sort((a, b) => (b.rssi ?? -999) - (a.rssi ?? -999));

        // GPS position
        const lat = evt.decoded?.lat;
        const lng = evt.decoded?.lng;
        if (lat && lng) {
            return [{ lat, lng, rssi, snr, gatewayId, allGateways, type: 'gps', at: evt.at }];
        }

        // WiFi position — pre-resolved via fingerprint locate API
        const wc = wifiCoords?.get(i);
        if (wc) {
            return [{ lat: wc.lat, lng: wc.lng, rssi, snr, gatewayId, allGateways, type: 'wifi', at: evt.at }];
        }

        // BLE position
        const bleItems = evt.decoded?.ble ?? [];
        const coords = bleToCoords(bleItems, locations);
        if (!coords) return [];
        const title = matchBle(bleItems, locations);
        return [{ lat: coords.latitude, lng: coords.longitude, rssi, snr, gatewayId, allGateways, type: 'ble', at: evt.at, location: title }];
    });
}

function toDateInputValue(date) {
    return date.toISOString().slice(0, 10);
}

// Rough bounds --> center + zoom calculation for pigeon-maps
function fitToPoints(points) {
    if (!points.length) return null;
    const lats = points.map(p => p.lat);
    const lngs = points.map(p => p.lng);
    const center = [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
    const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
    const zoom = spread < 0.0005 ? 19 : spread < 0.002 ? 18 : spread < 0.01 ? 16 : spread < 0.05 ? 14 : spread < 0.2 ? 12 : 10;
    return { center, zoom };
}

export default function AssetTrackingCoverage({ devices, gateways = [] }) {
    const airportCoords = useSelector(SelectCurrentCoordinates);
    const locations = useSelector(selectAirportLocations) ?? [];
    const defaultCenter = airportCoords?.lat ? [airportCoords.lat, airportCoords.lon] : [1.3521, 103.8198];

    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [mapZoom, setMapZoom] = useState(16);
    const [satelliteView, setSatelliteView] = useState(false);

    const [selectedDevice, setSelectedDevice] = useState(null);
    const [colourMode, setColourMode] = useState('rssi');
    const [loading, setLoading] = useState(false);
    const [points, setPoints] = useState([]);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [tooltip, setTooltip] = useState(null); // { pt, x, y }

    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return toDateInputValue(d);
    });
    const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));

    const filteredDeviceList = useMemo(() =>
        (devices ?? []).filter(d =>
            !search || d.meta?.deviceName?.toLowerCase().includes(search.toLowerCase())
        ),
        [devices, search]
    );

    const stats = useMemo(() => {
        if (!points.length) return null;
        const gps  = points.filter(p => p.type === 'gps').length;
        const ble  = points.filter(p => p.type === 'ble').length;
        const wifi = points.filter(p => p.type === 'wifi').length;
        const rssiPts = points.filter(p => p.rssi !== null);
        const snrPts = points.filter(p => p.snr !== null);
        const avgRssi = rssiPts.length ? Math.round(rssiPts.reduce((s, p) => s + p.rssi, 0) / rssiPts.length) : null;
        const avgSnr = snrPts.length ? (snrPts.reduce((s, p) => s + p.snr, 0) / snrPts.length).toFixed(1) : null;
        return { total: points.length, gps, ble, wifi, avgRssi, avgSnr };
    }, [points]);

    async function loadCoverage() {
        if (!selectedDevice) return;
        setLoading(true);
        setError(null);
        setPoints([]);
        setTooltip(null);
        try {
            const tenantId = selectedDevice.meta?.tenantId;
            const appName = selectedDevice.meta?.applicationName;
            const devEui = selectedDevice.id;
            const start = new Date(startDate + 'T00:00:00');
            const end = new Date(endDate + 'T23:59:59');
            const events = await fetchDeviceEvents(tenantId, appName, devEui, start, end);
            const wifiCoords = await resolveWifiEventsToCoords(events, selectedDevice.meta?.tenantName);
            const processed = processEvents(events, locations, wifiCoords);
            setPoints(processed);
            const fit = fitToPoints(processed);
            if (fit) {
                setMapCenter(fit.center);
                setMapZoom(fit.zoom);
            }
            if (processed.length === 0) {
                setError('No coverage data found for this period. Check the browser console if this is the first query, a Firestore index creation link will appear there.');
            }
        } catch (e) {
            console.error('Coverage load error:', e);
            setError('Failed to load data. If this is the first query, open the browser console for a Firestore index creation link, then retry.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ display: 'flex', height: '89vh', gap: 2 }}>

            {/* ── Left panel ── */}
            <div style={{
                width: 250, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10,
                background: '#fff', border: '1px solid #dee2e6', borderRadius: 8,
                padding: 12, overflowY: 'auto', flexShrink: 0
            }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#495057' }}>Select Tracker</div>

                <input
                    type="text"
                    placeholder="Search trackers..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 12, outline: 'none' }}
                />

                <div style={{ overflowY: 'auto', maxHeight: 190 }}>
                    {filteredDeviceList.map(device => (
                        <div
                            key={device.id}
                            onClick={() => { setSelectedDevice(device); setPoints([]); setError(null); setTooltip(null); }}
                            style={{
                                padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                                background: selectedDevice?.id === device.id ? '#e7f1ff' : 'transparent',
                                fontWeight: selectedDevice?.id === device.id ? 600 : 400,
                                borderLeft: selectedDevice?.id === device.id ? '3px solid #0d6efd' : '3px solid transparent',
                                marginBottom: 2, userSelect: 'none'
                            }}
                        >
                            {device.meta?.deviceName || device.id}
                        </div>
                    ))}
                    {filteredDeviceList.length === 0 && (
                        <div style={{ fontSize: 11, color: '#adb5bd', padding: 8 }}>No trackers found</div>
                    )}
                </div>

                <hr style={{ margin: '2px 0' }} />

                <div style={{ fontWeight: 600, fontSize: 13, color: '#495057' }}>Date Range</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 11, color: '#6c757d', marginBottom: 0 }}>From</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        style={{ fontSize: 12, border: '1px solid #dee2e6', borderRadius: 4, padding: '3px 6px' }}
                    />
                    <label style={{ fontSize: 11, color: '#6c757d', marginBottom: 0 }}>To</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        style={{ fontSize: 12, border: '1px solid #dee2e6', borderRadius: 4, padding: '3px 6px' }}
                    />
                </div>

                <hr style={{ margin: '2px 0' }} />

                <div style={{ fontWeight: 600, fontSize: 13, color: '#495057' }}>Colour By</div>
                <div style={{ display: 'flex', gap: 6 }}>
                    {['rssi', 'snr'].map(mode => (
                        <button
                            key={mode}
                            onClick={() => setColourMode(mode)}
                            style={{
                                flex: 1, padding: '5px 0', fontSize: 12,
                                border: `1px solid ${colourMode === mode ? '#0d6efd' : '#dee2e6'}`,
                                borderRadius: 4, cursor: 'pointer',
                                background: colourMode === mode ? '#0d6efd' : '#fff',
                                color: colourMode === mode ? '#fff' : '#495057',
                                fontWeight: colourMode === mode ? 600 : 400
                            }}
                        >
                            {mode.toUpperCase()}
                        </button>
                    ))}
                </div>

                <button
                    onClick={loadCoverage}
                    disabled={!selectedDevice || loading}
                    style={{
                        padding: '7px 0',
                        background: (!selectedDevice || loading) ? '#dee2e6' : '#198754',
                        color: (!selectedDevice || loading) ? '#6c757d' : '#fff',
                        border: 'none', borderRadius: 4, fontWeight: 600,
                        fontSize: 13, cursor: (!selectedDevice || loading) ? 'not-allowed' : 'pointer'
                    }}
                >
                    {loading ? 'Loading...' : 'Load Coverage'}
                </button>

                {/* Stats */}
                {stats && (
                    <div style={{
                        fontSize: 11, color: '#495057',
                        borderTop: '1px solid #dee2e6', paddingTop: 8,
                        display: 'flex', flexDirection: 'column', gap: 3
                    }}>
                        <div><b>Total uplinks:</b> {stats.total}</div>
                        <div><b>GPS:</b> {stats.gps} &nbsp;|&nbsp; <b>BLE:</b> {stats.ble} &nbsp;|&nbsp; <b>WiFi:</b> {stats.wifi}</div>
                        {stats.avgRssi !== null && <div><b>Avg RSSI:</b> {stats.avgRssi} dBm</div>}
                        {stats.avgSnr !== null && <div><b>Avg SNR:</b> {stats.avgSnr} dB</div>}
                    </div>
                )}

                {error && (
                    <div style={{ fontSize: 11, color: '#dc3545', paddingTop: 4, lineHeight: 1.4 }}>
                        {error}
                    </div>
                )}
            </div>

            {/* ── Map panel ── */}
            <div className='border' style={{ flex: 1, position: 'relative', borderRadius: 8, overflow: 'hidden' }}>

                {/* Empty state overlay */}
                {!selectedDevice && (
                    <div style={{
                        position: 'absolute', zIndex: 10, inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,255,255,0.85)', borderRadius: 8, pointerEvents: 'none'
                    }}>
                        <div style={{ textAlign: 'center', color: '#6c757d' }}>
                            <div style={{ fontSize: 36, marginBottom: 8 }}>🛜</div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>Select a tracker to view coverage</div>
                            <div style={{ fontSize: 12, marginTop: 4 }}>Pick a date range and click Load Coverage</div>
                        </div>
                    </div>
                )}

                {/* Satellite toggle */}
                <div style={{
                    position: 'absolute', top: 10, right: 10, zIndex: 10,
                    background: 'rgba(255,255,255,0.95)', borderRadius: 6,
                    border: '1px solid #dee2e6', padding: '4px 10px',
                    fontSize: 12, cursor: 'pointer', userSelect: 'none'
                }} onClick={() => setSatelliteView(v => !v)}>
                    {satelliteView ? 'Street View' : 'Satellite'}
                </div>

                <Map
                    center={mapCenter}
                    zoom={mapZoom}
                    provider={satelliteView ? SATELLITE_PROVIDER : OSM_PROVIDER}
                    onBoundsChanged={({ center, zoom }) => { setMapCenter(center); setMapZoom(zoom); }}
                    style={{ height: '100%', borderRadius: 8, border: '1px solid #dee2e6' }}
                >
                    {points.map((pt, i) => {
                        const colour = colourMode === 'rssi' ? rssiScale(pt.rssi) : snrScale(pt.snr);
                        return (
                            <Overlay key={i} anchor={[pt.lat, pt.lng]} offset={[7, 7]}>
                                <div
                                    onMouseEnter={e => setTooltip({ pt, x: e.clientX, y: e.clientY })}
                                    onMouseMove={e => setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
                                    onMouseLeave={() => setTooltip(null)}
                                    style={{
                                        width: 14, height: 14, borderRadius: '50%',
                                        background: colour,
                                        border: '1px solid rgba(0, 0, 0, 0.99)',
                                        cursor: 'crosshair',
                                        opacity: 0.9
                                    }}
                                />
                            </Overlay>
                        );
                    })}
                </Map>

                {/* Hover tooltip */}
                {tooltip && (
                    <div style={{
                        position: 'fixed',
                        left: tooltip.x + 12,
                        top: tooltip.y - 50,
                        background: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: 5,
                        padding: '5px 10px',
                        fontSize: 12,
                        zIndex: 9999,
                        pointerEvents: 'none',
                        lineHeight: 1.6,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <span style={{
                                background: tooltip.pt.type === 'gps' ? '#dcfce7' : tooltip.pt.type === 'wifi' ? '#ede9fe' : '#dbeafe',
                                color:      tooltip.pt.type === 'gps' ? '#15623a' : tooltip.pt.type === 'wifi' ? '#5b21b6' : '#1e4fa0',
                                fontWeight: 700, fontSize: 11, borderRadius: 4, padding: '1px 6px'
                            }}>
                                {tooltip.pt.type === 'gps' ? 'GPS' : tooltip.pt.type === 'wifi' ? 'WiFi' : 'BLE'}
                            </span>
                            {tooltip.pt.location && <span style={{ color: '#374151' }}>{tooltip.pt.location}</span>}
                        </div>
                        <div style={{ color: '#6b7280', marginBottom: 4 }}>{tsToDate(tooltip.pt.at).toLocaleString()}</div>
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 4, marginTop: 2 }}>
                            <div style={{ fontWeight: 600, fontSize: 11, color: '#374151', marginBottom: 3 }}>
                                Gateways ({(tooltip.pt.allGateways ?? []).length})
                            </div>
                            {(tooltip.pt.allGateways ?? []).map((gw, gi) => (
                                <div key={gi} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, fontSize: 11 }}>
                                    {gi === 0 && <span style={{ color: '#f59e0b', fontSize: 10 }}>★</span>}
                                    {gi > 0  && <span style={{ width: 10 }} />}
                                    <span style={{ color: '#1B4F8A', fontWeight: 600 }}>
                                        {resolveGatewayName(gw.gatewayId, gateways)}
                                    </span>
                                    <span style={{ color: '#6b7280' }}>
                                        {gw.rssi != null ? `${gw.rssi} dBm` : '—'}
                                        {gw.snr  != null ? ` / ${gw.snr} dB` : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Colour legend */}
                <div style={{
                    position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(255,255,255,0.95)', padding: '6px 14px',
                    borderRadius: 6, border: '1px solid #dee2e6', zIndex: 5,
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 11, color: '#6c757d', whiteSpace: 'nowrap'
                }}>
                    <span>{colourMode === 'rssi' ? 'Weak  −120 dBm' : 'Weak  −20 dB'}</span>
                    <div style={{
                        width: 120, height: 10, borderRadius: 4,
                        background: 'linear-gradient(to right, #d73027, #fee08b, #1a9850)'
                    }} />
                    <span>{colourMode === 'rssi' ? '−50 dBm  Strong' : '+15 dB  Strong'}</span>
                </div>
            </div>

        </div>
    );
}
